import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import SchemaFormView from "@/components/Schema/SchemaFormView";

interface SchemaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  schemaInfo: unknown;
  isLoading?: boolean;
  isSubmitting?: boolean;
  onSave: () => void;
  onCancel: () => void;
  /** "premium" = rounded-2xl, scrollable body, bordered header/footer */
  variant?: "default" | "premium";
  /** Custom save button label. Default: "Save" */
  saveLabel?: string;
  /** Optional icon rendered before the save label */
  saveIcon?: React.ReactNode;
  errorMessage?: React.ReactNode;
  /** Secondary loading state (e.g. fetching the record for an edit dialog) */
  isDetailLoading?: boolean;
  /** Whether the record fetch failed */
  isDetailError?: boolean;
  /** Optional icon node rendered left of title in the premium header */
  headerIcon?: React.ReactNode;
  /** Override max-width for premium variant (default "sm:max-w-[720px]") */
  maxWidth?: string;
}

/**
 * Reusable dialog for Formily schema-driven add / edit forms.
 *
 * variant="default"  — simple dialog (AdminSettings SLA / Escalation / Business Hours)
 * variant="premium"  — rounded-2xl with scrollable body and bordered header/footer
 *                      (AdminCompanies, AdminCompanyMembers)
 */
export function SchemaFormDialog({
  open,
  onOpenChange,
  title,
  description,
  form,
  schemaInfo,
  isLoading = false,
  isSubmitting = false,
  onSave,
  onCancel,
  variant = "default",
  saveLabel,
  saveIcon,
  errorMessage,
  isDetailLoading = false,
  isDetailError = false,
  headerIcon,
  maxWidth,
}: SchemaFormDialogProps) {
  const isBusy = isLoading || isDetailLoading;
  const canSave = !isSubmitting && !isBusy && !isDetailError && Boolean(schemaInfo);

  if (variant === "premium") {
    return (
      <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "gap-0 overflow-hidden rounded-2xl p-0 shadow-xl",
            maxWidth ?? "sm:max-w-[720px]"
          )}
        >
          <DialogHeader className="border-b bg-muted/30 px-7 pb-4 pt-7 text-left">
            {headerIcon ? (
              <div className="mb-1 flex items-center gap-3">
                {headerIcon}
                <DialogTitle className="text-xl font-bold tracking-tight">
                  {title}
                </DialogTitle>
              </div>
            ) : (
              <DialogTitle className="text-xl font-bold tracking-tight">
                {title}
              </DialogTitle>
            )}
            {description && (
              <DialogDescription
                className={cn(
                  "text-sm leading-relaxed text-muted-foreground",
                  headerIcon && "pl-12"
                )}
              >
                {description}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="max-h-[65vh] overflow-y-auto px-7 py-6">
            {isBusy && (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            )}
            {isDetailError && !isBusy && Boolean(schemaInfo) && (
              <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {errorMessage ?? "Could not load this record for editing."}
              </p>
            )}
            {!isBusy && !isDetailError && Boolean(schemaInfo) && (
              <SchemaFormView
                form={form}
                schemaJson={(schemaInfo as any)?.json ?? schemaInfo}
              />
            )}
          </div>

          <DialogFooter className="gap-3 border-t bg-muted/20 px-7 py-5 sm:justify-end">
            <Button
              variant="outline"
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="min-w-[96px] rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onSave}
              disabled={!canSave}
              className={cn("min-w-[120px] rounded-lg", saveIcon && "gap-2")}
            >
              {saveIcon}
              {isSubmitting ? "Saving..." : (saveLabel ?? "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // default variant — existing behaviour used by AdminSettings
  return (
    <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : (
          <SchemaFormView
            form={form}
            schemaJson={(schemaInfo as any)?.json ?? schemaInfo}
          />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button disabled={isSubmitting || isLoading} onClick={onSave}>
            {isSubmitting ? "Saving..." : (saveLabel ?? "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
